import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { notification } from 'antd';
import { getEducationClassList, type EducationClass } from 'services/api';
import { createCurriculum, getCurriculumById, updateCurriculum } from 'services/api/educationPlans';
import { getSubjectsList, type Subject } from 'services/api/subjects';
import { getEducationPlanUrl, type EducationPlanPageParams } from 'services/navigation';
import type { Curriculum, CurriculumElement } from 'services/api/educationPlans';
import type { EducationPlanRow } from '../columns';
import { ADD_SUBJECT_ROW_KEY, TOTAL_ROW_KEY, getColumns } from '../columns';
import { getClassKey, mergeTableClasses } from '../utils';

type UseEducationPlanResult = {
  state: {
    loading: boolean;
    columns: ReturnType<typeof getColumns>;
    dataSource: EducationPlanRow[];
    subjects: Subject[];
    educationClasses: EducationClass[];
    selectedClassKeys: string[];
    classesModalVisible: boolean;
    editMode: boolean;
    curriculumName: string;
    readonlyMode: boolean;
  };
  methods: {
    handleAddSubjectRow: () => void;
    handleSetSelectedClasses: (classKeys: string[]) => void;
    handleOpenClassesModal: () => void;
    handleCloseClassesModal: () => void;
    handleToggleEditMode: (value: boolean) => void;
    handleSetCurriculumName: (name: string) => void;
    handleHoursChange: (rowKey: string, classId: number, hours: number | null) => void;
  };
};

export const useEducationPlan = (): UseEducationPlanResult => {
  const { educationPlanId } = useParams<EducationPlanPageParams>();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [educationClasses, setEducationClasses] = useState<EducationClass[]>([]);
  const [selectedClassKeys, setSelectedClassKeys] = useState<string[]>([]);
  const [rows, setRows] = useState<EducationPlanRow[]>([]);
  const [classesModalVisible, setClassesModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [curriculumName, setCurriculumName] = useState('');
  const [readonlyMode, setReadonlyMode] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    if (!educationPlanId) {
      return;
    }

    setLoading(true);
    if (educationPlanId === 'new') {
      setReadonlyMode(false);
      setEditMode(true);
      const fromId = searchParams.get('from');

      if (!fromId) {
        Promise.all([
          getSubjectsList(),
          getEducationClassList({ sort: ['parallel,asc', 'litera,asc'], size: 1000 }),
        ])
          .then(([subjectsRes, classesRes]) => {
            const subjectsData: Subject[] = subjectsRes.content;
            const classesData: EducationClass[] = classesRes.content;

            setSubjects(subjectsData);
            setEducationClasses(classesData);
            setRows([]);
          })
          .finally(() => setLoading(false));
        return;
      }

      Promise.all([
        getSubjectsList(),
        getEducationClassList({ sort: ['parallel,asc', 'litera,asc'], size: 1000 }),
        getCurriculumById(fromId),
      ])
        .then(([subjectsRes, classesRes, curriculumRes]) => {
          const subjectsData: Subject[] = subjectsRes.content;
          const classesData: EducationClass[] = classesRes.content;
          const elements = curriculumRes.items as unknown as CurriculumElement[];
          const mergedClasses = mergeTableClasses(classesData, elements);

          setCurriculumName(`${curriculumRes.name} (копия)`);
          setSubjects(subjectsData);
          setEducationClasses(mergedClasses);

          const elementClassKeys = new Set(elements.map((el) => getClassKey(el)));
          const initialSelectedKeys = mergedClasses
            .map((cls) => getClassKey(cls))
            .filter((key) => elementClassKeys.has(key));
          setSelectedClassKeys(initialSelectedKeys);

          const groupedBySubject = elements.reduce<Record<number, CurriculumElement[]>>(
            (acc, el) => {
              if (!acc[el.subjectId]) {
                acc[el.subjectId] = [];
              }
              acc[el.subjectId].push(el);
              return acc;
            },
            {},
          );

          const initialRows: EducationPlanRow[] = Object.entries(groupedBySubject).map(
            ([subjectIdStr, curriculumElements]) => {
              const subjectId = Number(subjectIdStr);
              const subject = subjectsData.find((s: Subject) => s.id === subjectId) || null;

              const row: EducationPlanRow = {
                key: `subject-${subjectId}`,
                subjectId,
                subjectName: subject ? subject.name : null,
                curriculumElements,
              };

              mergedClasses.forEach((cls: EducationClass) => {
                const classKey = String(cls.id);
                const elementForClass = curriculumElements.find(
                  (el) => el.parallel === cls.parallel && el.litera === cls.litera,
                );
                row[classKey] = elementForClass ? elementForClass.hours : 0;
              });

              return row;
            },
          );

          setRows(initialRows);
        })
        .finally(() => setLoading(false));

      return;
    }

    Promise.all([
      getSubjectsList(),
      getEducationClassList({ sort: ['parallel,asc', 'litera,asc'], size: 1000 }),
      getCurriculumById(educationPlanId),
    ])
      .then(([subjectsRes, classesRes, curriculumRes]) => {
        const subjectsData: Subject[] = subjectsRes.content;
        const classesData: EducationClass[] = classesRes.content;
        const elements = curriculumRes.items as unknown as CurriculumElement[];
        const mergedClasses = mergeTableClasses(classesData, elements);

        if (curriculumRes.source === 'USER_CREATED') {
          setReadonlyMode(false);
          setEditMode(true);
        }

        setCurriculumName(curriculumRes.name);
        setSubjects(subjectsData);
        setEducationClasses(mergedClasses);

        const elementClassKeys = new Set(elements.map((el) => getClassKey(el)));
        const initialSelectedKeys = mergedClasses
          .map((cls) => getClassKey(cls))
          .filter((key) => elementClassKeys.has(key));
        setSelectedClassKeys(initialSelectedKeys);

        const groupedBySubject = elements.reduce<Record<number, CurriculumElement[]>>((acc, el) => {
          if (!acc[el.subjectId]) {
            acc[el.subjectId] = [];
          }
          acc[el.subjectId].push(el);
          return acc;
        }, {});

        const initialRows: EducationPlanRow[] = Object.entries(groupedBySubject).map(
          ([subjectIdStr, curriculumElements]) => {
            const subjectId = Number(subjectIdStr);
            const subject = subjectsData.find((s: Subject) => s.id === subjectId) || null;

            const row: EducationPlanRow = {
              key: `subject-${subjectId}`,
              subjectId,
              subjectName: subject ? subject.name : null,
              curriculumElements,
            };

            mergedClasses.forEach((cls: EducationClass) => {
              const classKey = String(cls.id);
              const elementForClass = curriculumElements.find(
                (el) => el.parallel === cls.parallel && el.litera === cls.litera,
              );
              row[classKey] = elementForClass ? elementForClass.hours : 0;
            });

            return row;
          },
        );

        setRows(initialRows);
      })
      .finally(() => setLoading(false));
  }, [educationPlanId]);

  const selectedClasses = useMemo(
    () => educationClasses.filter((cls) => selectedClassKeys.includes(getClassKey(cls))),
    [educationClasses, selectedClassKeys],
  );

  useEffect(() => {
    setRows((prev) =>
      prev.map((row) => {
        const updatedRow: EducationPlanRow = { ...row };

        selectedClasses.forEach((cls: EducationClass) => {
          const classKey = String(cls.id);
          const elementForClass = row.curriculumElements.find(
            (el) => el.parallel === cls.parallel && el.litera === cls.litera,
          );
          updatedRow[classKey] = elementForClass ? elementForClass.hours : 0;
        });

        return updatedRow;
      }),
    );
  }, [selectedClasses]);

  const handleSubjectChange = (rowKey: string, subjectId: number) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.key !== rowKey) {
          return row;
        }
        const subject = subjects.find((s) => s.id === subjectId) || null;
        return {
          ...row,
          subjectId,
          subjectName: subject ? subject.name : null,
        };
      }),
    );
  };

  const handleDeleteRow = (rowKey: string) => {
    setRows((prev) => prev.filter((row) => row.key !== rowKey));
  };

  const handleAddSubjectRow = () => {
    const newRow: EducationPlanRow = {
      key: `subject-${Date.now()}-${Math.random()}`,
      subjectId: null,
      subjectName: null,
      curriculumElements: [] as CurriculumElement[],
    };

    selectedClasses.forEach((cls) => {
      const classKey = String(cls.id);
      newRow[classKey] = 0;
    });

    setRows((prev) => [...prev, newRow]);
  };

  const handleSetSelectedClasses = (keys: string[]) => {
    setSelectedClassKeys(keys);
    const selectedSet = new Set(keys);
    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        curriculumElements: row.curriculumElements.filter((el) => selectedSet.has(getClassKey(el))),
      })),
    );
  };

  const handleRemoveClass = (classKey: string) => {
    setSelectedClassKeys((prev) => prev.filter((k) => k !== classKey));
    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        curriculumElements: row.curriculumElements.filter((el) => getClassKey(el) !== classKey),
      })),
    );
  };

  const handleOpenClassesModal = () => {
    setClassesModalVisible(true);
  };

  const handleCloseClassesModal = () => {
    setClassesModalVisible(false);
  };

  const handleSetCurriculumName = (name: string) => {
    setCurriculumName(name);
  };

  const handleHoursChange = (rowKey: string, classId: number, hours: number | null) => {
    const cls = educationClasses.find((c) => c.id === classId);
    if (!cls) {
      return;
    }

    setRows((prev) =>
      prev.map((row) => {
        if (row.key !== rowKey) {
          return row;
        }

        const classKey = String(classId);
        const subjectId = row.subjectId;

        const updatedRow: EducationPlanRow = {
          ...row,
          [classKey]: hours,
        };

        if (!subjectId) {
          return updatedRow;
        }

        const subject = subjects.find((s) => s.id === subjectId);
        const subjectAreaId = subject?.subjectAreaId ?? 0;

        const existingIndex = row.curriculumElements.findIndex(
          (el) =>
            el.parallel === cls.parallel && el.litera === cls.litera && el.subjectId === subjectId,
        );

        const nextElements = [...row.curriculumElements];

        if (hours === null || hours <= 0) {
          if (existingIndex !== -1) {
            nextElements.splice(existingIndex, 1);
          }
        } else {
          if (existingIndex !== -1) {
            nextElements[existingIndex] = {
              ...nextElements[existingIndex],
              hours,
            };
          } else {
            nextElements.push({
              id: 0,
              subjectId,
              subjectAreaId,
              litera: cls.litera,
              parallel: cls.parallel,
              hours,
            });
          }
        }

        updatedRow.curriculumElements = nextElements;

        return updatedRow;
      }),
    );
  };

  const handleSubmit = () => {
    if (!educationPlanId) {
      return;
    }

    if (educationPlanId === 'new') {
      const payload: Curriculum = {
        name: curriculumName,
        items: rows.flatMap((row) => row.curriculumElements),
      };
      createCurriculum(payload).then((res) => {
        notification.success({ message: 'Учебный план успешно создан' });
        navigate(getEducationPlanUrl({ educationPlanId: res?.id?.toString() ?? '' }));
      });
      return;
    }

    const payload: Curriculum = {
      name: curriculumName,
      items: rows.flatMap((row) => row.curriculumElements),
    };
    updateCurriculum(educationPlanId, payload);
  };

  const totalRow = useMemo(() => {
    if (!rows.length) {
      return null;
    }

    const row: EducationPlanRow = {
      key: TOTAL_ROW_KEY,
      subjectId: null,
      subjectName: null,
      curriculumElements: [] as CurriculumElement[],
    };

    selectedClasses.forEach((cls) => {
      const classKey = String(cls.id);
      const total = rows.reduce((sum, currentRow) => {
        const value = currentRow[classKey];
        const numeric = typeof value === 'number' ? value : 0;
        return sum + numeric;
      }, 0);

      row[classKey] = total;
    });

    return row;
  }, [rows, selectedClasses]);

  const dataSource = useMemo(() => {
    const baseRows: EducationPlanRow[] = [...rows];

    if (totalRow) {
      baseRows.push(totalRow);
    }

    if (!editMode) {
      return baseRows;
    }

    const addRow: EducationPlanRow = {
      key: ADD_SUBJECT_ROW_KEY,
      subjectId: null,
      subjectName: null,
      curriculumElements: [] as CurriculumElement[],
    };

    return [...baseRows, addRow];
  }, [rows, editMode, totalRow]);

  const handleToggleEditMode = (value: boolean) => {
    if (!value) {
      const trimmedName = curriculumName.trim();
      if (!trimmedName) {
        notification.warning({
          message: 'Название учебного плана',
          description: 'Заполните название перед сохранением.',
        });
        return;
      }
      handleSubmit();
      setClassesModalVisible(false);
    }
    setEditMode(value);
  };

  const columns = useMemo(
    () =>
      getColumns({
        classes: selectedClasses,
        subjects,
        selectedSubjectIds: rows
          .map((row) => row.subjectId)
          .filter((id): id is number => id !== null),
        onSubjectChange: handleSubjectChange,
        onDeleteRow: handleDeleteRow,
        onAddSubjectRow: handleAddSubjectRow,
        onAddClassClick: handleOpenClassesModal,
        onHoursChange: handleHoursChange,
        onRemoveClass: handleRemoveClass,
        editMode,
      }),
    [selectedClasses, subjects, rows, editMode],
  );

  return {
    state: {
      loading,
      columns,
      dataSource,
      subjects,
      educationClasses,
      selectedClassKeys,
      classesModalVisible,
      editMode,
      curriculumName,
      readonlyMode,
    },
    methods: {
      handleAddSubjectRow,
      handleSetSelectedClasses,
      handleOpenClassesModal,
      handleCloseClassesModal,
      handleToggleEditMode,
      handleSetCurriculumName,
      handleHoursChange,
    },
  };
};
